// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import clsx from 'clsx';
import history from 'src/history';
import { DefaultButton, IComboBoxOption } from 'office-ui-fabric-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    resetSelectedShippingOfferData,
    resetSelectedOffer,
    Progress,
    getShippingDeviceOfferData,
    getShippingAccessoryOfferData,
    reActivateOrderStatusSelector,
    reasonForNotSelectingOffers,
    reasonForZ4RReturn
} from './../../../store/orderCreation';
import {
    updateCreateOrderProgress,
    resetRegistrationData,
    GET_REGISTER_INFO_DATA,
    resetPartsStatus,
    resetParts,
} from './../../../store/orderCreation/actions';
import SelectedRepairItemsDetails from './SelectedRepairItemsDetails';
import {
    allSelectedOfferSelector,
    isOnSiteRepairStatusSelector,
    parentDeviceOfferSelector,
    partAndAccessoryOfferSelector,
    secondSRCreationSelector,
    softwareRepairOfferSelector
} from './../../../store/orderCreation/selectors';
import { resetActionsData } from './../../../store/actionStatus';

import DeviceServiceOptions from './ServiceOptions';

import Styles from './styles.module.scss';
import { logPageAction } from 'src/lib/ApplicationInsights';
import { clearSafetyAttachmentInfo, CLEAR_SAFETY_ATTACHMENT_INFO } from './../../../store/attachments';
import { getOnsiteSoldToShipTo } from './../../../store/managePartners';

export default React.forwardRef(RepairOffer);

function RepairOffer(props: any, ref: any) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const selectedRepairOffers = useSelector(state => allSelectedOfferSelector(state));
    const parentDeviceOffer: any = useSelector(state => parentDeviceOfferSelector(state));
    const softwareOrderOffer: any = useSelector(state => softwareRepairOfferSelector(state));
    const partAndAccessoryOffers: [] = useSelector(state => partAndAccessoryOfferSelector(state));
    const reActivateOrder = useSelector(state => reActivateOrderStatusSelector(state));
    const secondSRCreation = useSelector(state => secondSRCreationSelector(state));
    const isOnSiteRepair = useSelector(state => isOnSiteRepairStatusSelector(state));

    // Per line item state. selectionMap holds the kind of "no repair" selection
    // ('donotrepair' | 'z4r') for each line item that needs a reason.
    const [selectionMap, setSelectionMap] = React.useState<{ [key: string]: 'donotrepair' | 'z4r' }>({});
    const [reasonMap, setReasonMap] = React.useState<{ [key: string]: IComboBoxOption | undefined }>({});
    const [commentsMap, setCommentsMap] = React.useState<{ [key: string]: string }>({});

    function onNoRepairSelectionChange(lineItemId: string, type?: 'donotrepair' | 'z4r') {
        setSelectionMap(prev => {
            const next = { ...prev };
            if (type) {
                next[lineItemId] = type;
            } else {
                delete next[lineItemId];
            }
            return next;
        });
        if (!type) {
            setReasonMap(prev => {
                const next = { ...prev };
                delete next[lineItemId];
                return next;
            });
            setCommentsMap(prev => {
                const next = { ...prev };
                delete next[lineItemId];
                return next;
            });
        }
    }

    function onChangeSelectionClick() {
        logPageAction('Repair offers change selection button clicked');

        dispatch(resetSelectedOffer());
        dispatch(resetRegistrationData());
        dispatch(resetActionsData(GET_REGISTER_INFO_DATA));
        dispatch(updateCreateOrderProgress(Progress.ItemSelection));
        dispatch(resetPartsStatus());
        dispatch(resetParts());
        dispatch(clearSafetyAttachmentInfo());
        dispatch(resetActionsData(CLEAR_SAFETY_ATTACHMENT_INFO));

        if (secondSRCreation) {
            history.push(`/device/orderDetail`);
        }
    }

    function onSubmit() {
        logPageAction('Continue to shipping address: Continue button clicked');

        const z4rReturnReasons = Object.keys(selectionMap)
            .filter(id => selectionMap[id] === 'z4r')
            .map(id => ({
                accessoryType: id,
                reason: reasonMap[id]?.text,
                repairComments: commentsMap[id] || '',
            }));

        // Backend supports a single order-level ReasonForNoRepair, applied only when the
        // whole order has no repairs. Send it from the do-not-repair line's reason.
        const doNotRepairIds = Object.keys(selectionMap).filter(id => selectionMap[id] === 'donotrepair');
        const hasAnyRealOffer = selectedRepairOffers &&
            Object.values(selectedRepairOffers).some((offer: any) => Object.keys(offer).length > 1);
        const orderLevelDoNotRepair = (!hasAnyRealOffer && doNotRepairIds.length)
            ? {
                repairReason: reasonMap[doNotRepairIds[0]],
                repairComments: commentsMap[doNotRepairIds[0]] || '',
            }
            : {
                repairReason: undefined,
                repairComments: '',
            };

        dispatch(reasonForNotSelectingOffers(orderLevelDoNotRepair));
        dispatch(reasonForZ4RReturn(z4rReturnReasons));

        dispatch(updateCreateOrderProgress(Progress.UpdateDeviceNewSerialNumber));

        dispatch(getShippingDeviceOfferData());
        dispatch(getShippingAccessoryOfferData());
        dispatch(resetSelectedShippingOfferData());//RESET previously selected shipping offers

        if (isOnSiteRepair) {
            dispatch(getOnsiteSoldToShipTo());
        }
    }

    function numberOfItemHasOffer() {
        const numberOfPartAndAccessoryHasOffer = Array.isArray(partAndAccessoryOffers) ? partAndAccessoryOffers.filter((item: any) => item.offers.length > 0).length : 0;
        const parentHasOffer = parentDeviceOffer && parentDeviceOffer.hasOwnProperty('offers') && parentDeviceOffer.offers.length > 0 ? 1 : 0;
        const softwareOrderHasOffer = (softwareOrderOffer && softwareOrderOffer.hasOwnProperty('offers') && softwareOrderOffer.offers.length > 0) ? 1 : 0;
        return numberOfPartAndAccessoryHasOffer + parentHasOffer + softwareOrderHasOffer;

    }

    function isFormSubmittable() {
        const allItemsSelected = selectedRepairOffers &&
            Object.keys(selectedRepairOffers).length === numberOfItemHasOffer();

        if (!reActivateOrder) {
            return (
                allItemsSelected &&
                Object.values(selectedRepairOffers).reduce((hasValue: boolean, offer: any) => hasValue || Object.keys(offer).length > 1, false)
            );
        }

        if (!allItemsSelected) {
            return false;
        }

        // Every do-not-repair or Z4R line item needs a reason and comments.
        // Z4R returns cannot use the 'lost in transit' reason.
        return Object.keys(selectionMap).every((id: string) => {
            const type = selectionMap[id];
            if (!type) {
                return true;
            }
            if (!reasonMap[id] || !commentsMap[id]) {
                return false;
            }
            return !(type === 'z4r' && reasonMap[id]?.key === 'offer_reason_lost_in_transit');
        });
    }

    const isContinueDisabled: boolean = !isFormSubmittable();

    const btnClassName = clsx(Styles.continueButton, {
        [Styles.disabled]: isContinueDisabled,
    });

    return (
        <div id="select_repair_items" className={Styles.repairItemContainer}>
            <header className={Styles.headerSection}>
                <h2 className={Styles.h2Heading}>{t('select_repair_items')}</h2>
                {!reActivateOrder &&
                    <DefaultButton
                        ariaLabel={t('change_repair_item_selection')}
                        className={Styles.changeSelection}
                        text={t('change_selection')}
                        onClick={onChangeSelectionClick}
                    />
                }
            </header>
            <SelectedRepairItemsDetails />
            <section className={Styles.offerTableContainer}>
                <DeviceServiceOptions
                    readonly={props.readonly}
                    ref={ref}
                    selectionMap={selectionMap}
                    onNoRepairSelectionChange={onNoRepairSelectionChange}
                    reasonMap={reasonMap}
                    commentsMap={commentsMap}
                    setReasonMap={setReasonMap}
                    setCommentsMap={setCommentsMap}
                />
            </section>

            {!props.readonly && (
                <section className={Styles.continueButtonContainer}>
                    <DefaultButton
                        className={btnClassName}
                        text={t('continue')}
                        disabled={isContinueDisabled}
                        onClick={onSubmit}
                    />
                </section>
            )}
        </div>
    );
}